import { InteractionRequiredAuthError, type IPublicClientApplication } from '@azure/msal-browser';
import { loginRequest } from './authConfig';
import type { EditorApi, Identity, QueryInfo, QueryResult, SaveResult, WikiListItem, WikiPageContent, WikiTree, WorkItemSummary } from './types';

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, public readonly detail?: string) {
    super(detail ?? code);
  }
}

export class ApiClient implements EditorApi {
  constructor(private readonly msal: IPublicClientApplication) {}

  private async token(): Promise<string> {
    const account = this.msal.getActiveAccount() ?? this.msal.getAllAccounts()[0];
    if (!account) throw new ApiError(401, 'not_signed_in');
    try {
      const res = await this.msal.acquireTokenSilent({ ...loginRequest, account });
      return res.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        const res = await this.msal.acquireTokenPopup({ ...loginRequest, account });
        return res.accessToken;
      }
      throw err;
    }
  }

  private async request<T>(path: string, init: RequestInit = {}, expectJson = true): Promise<T> {
    const token = await this.token();
    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${token}`);
    if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    const res = await fetch(path, { ...init, headers });
    if (!res.ok) {
      let code = 'http_error';
      let detail: string | undefined;
      try {
        const body = await res.json();
        code = body.error ?? code;
        detail = body.detail;
      } catch {
        /* no body */
      }
      throw new ApiError(res.status, code, detail);
    }
    if (!expectJson) return (await res.blob()) as unknown as T;
    return (await res.json()) as T;
  }

  private wikiBase(project: string, wiki: string) {
    return `/api/wikis/${encodeURIComponent(project)}/${encodeURIComponent(wiki)}`;
  }

  me() {
    return this.request<{ oid: string; name: string; upn: string }>('/api/me');
  }

  listWikis() {
    return this.request<WikiListItem[]>('/api/wikis');
  }

  openWiki(project: string, wiki: string) {
    return this.request<{ ok: true }>(`${this.wikiBase(project, wiki)}/open`, { method: 'POST' });
  }

  setFavorite(project: string, wiki: string, favorite: boolean) {
    return this.request<{ ok: true }>(`${this.wikiBase(project, wiki)}/favorite`, { method: 'PUT', body: JSON.stringify({ favorite }) });
  }

  getTree(project: string, wiki: string) {
    return this.request<WikiTree>(`${this.wikiBase(project, wiki)}/tree`);
  }

  getPage(project: string, wiki: string, path: string) {
    return this.request<WikiPageContent>(`${this.wikiBase(project, wiki)}/page?path=${encodeURIComponent(path)}`);
  }

  getPageEtag(project: string, wiki: string, path: string) {
    return this.request<{ etag: string }>(`${this.wikiBase(project, wiki)}/page/etag?path=${encodeURIComponent(path)}`);
  }

  async savePage(project: string, wiki: string, path: string, content: string, etag: string | undefined, comment?: string): Promise<SaveResult> {
    const token = await this.token();
    const res = await fetch(`${this.wikiBase(project, wiki)}/page?path=${encodeURIComponent(path)}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, etag, comment }),
    });
    if (res.status === 409) {
      const body = await res.json();
      return { ok: false, conflict: true, latest: body.latest as WikiPageContent };
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new ApiError(res.status, body.error ?? 'http_error', body.detail);
    }
    const body = await res.json();
    return { ok: true, etag: body.etag };
  }

  getAttachment(project: string, wiki: string, path: string) {
    return this.request<Blob>(`${this.wikiBase(project, wiki)}/attachment?path=${encodeURIComponent(path)}`, {}, false);
  }

  searchWorkItems(q: string, project: string) {
    return this.request<WorkItemSummary[]>(`/api/workitems/search?q=${encodeURIComponent(q)}&project=${encodeURIComponent(project)}`);
  }

  getWorkItems(ids: number[]) {
    if (ids.length === 0) return Promise.resolve([] as WorkItemSummary[]);
    return this.request<WorkItemSummary[]>(`/api/workitems?ids=${ids.join(',')}`);
  }

  searchIdentities(q: string) {
    return this.request<Identity[]>(`/api/identities?q=${encodeURIComponent(q)}`);
  }

  getIdentity(id: string) {
    return this.request<Identity>(`/api/identities/${encodeURIComponent(id)}`);
  }

  listQueries(project: string) {
    return this.request<QueryInfo[]>(`/api/projects/${encodeURIComponent(project)}/queries`);
  }

  runQuery(project: string, queryId: string) {
    return this.request<QueryResult>(`/api/projects/${encodeURIComponent(project)}/queries/${encodeURIComponent(queryId)}/results`);
  }
}
